import AddIcon from "@mui/icons-material/Add";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import EditIcon from "@mui/icons-material/Edit";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";

import ContractorAssistedRegistrationApi from "../../../../infrastructure/api/contractorAssistedRegistrationApi";
import ContractorTeamApi from "../../../../infrastructure/api/contractorTeamApi";
import EnhancedTable from "../../../components/shared/table";
import PartnerCommissionPageShell from "../../partner-commission/components/PartnerCommissionPageShell";
import {
  ASSISTED_REGISTRATION_NEW_QUERY,
  buildAssistedRegistrationSearchParams,
} from "../../partner-commission/assistedRegistrationRouteUtils";

const pick = (row, ...keys) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value != null && value !== "") return value;
  }
  return null;
};

const mapListRow = (item) => {
  const id = pick(item, "leadId", "LeadId");
  const requestConfirmTitle = pick(item, "requestConfirmTitle", "RequestConfirmTitle");
  const statusTitle = pick(item, "statusTitle", "StatusTitle");
  return {
    id,
    leadCode: pick(item, "leadCode", "LeadCode") || "—",
    fullName: pick(item, "fullName", "FullName") || pick(item, "maskedMobile", "MaskedMobile") || "—",
    maskedMobile: pick(item, "maskedMobile", "MaskedMobile") || "—",
    referenceID: pick(item, "referenceId", "ReferenceId") ?? "—",
    plantCapacityTitle: pick(item, "plantCapacityTitle", "PlantCapacityTitle") || "—",
    powerPlantTypeTitle: pick(item, "powerPlantTypeTitle", "PowerPlantTypeTitle") || "—",
    statusTitle: requestConfirmTitle || statusTitle || "—",
    stepTitle: pick(item, "stepTitle", "StepTitle") || "—",
    registrationStatusTitle: statusTitle || "—",
    updateOn: pick(item, "updateOn", "UpdateOn", "submittedAt", "SubmittedAt"),
    canEdit: Boolean(item?.canEdit ?? item?.CanEdit),
    isLocked: Boolean(item?.isLocked ?? item?.IsLocked),
  };
};

const ContractorAssistedRegistrationListPage = () => {
  const [, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [canManageTeam, setCanManageTeam] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [res, contextRes] = await Promise.all([
        ContractorAssistedRegistrationApi.getList(),
        ContractorTeamApi.getContext().catch(() => ({ isSuccess: false })),
      ]);
      if (contextRes?.isSuccess) {
        setCanManageTeam(Boolean(contextRes.data?.canManageTeam ?? contextRes.data?.CanManageTeam));
      } else {
        setCanManageTeam(false);
      }
      if (!res.isSuccess) {
        setError(res.message || "بارگذاری لیست ناموفق بود.");
        setRows([]);
        return;
      }
      const items = Array.isArray(res.data) ? res.data : [];
      setRows(items.map(mapListRow).filter((row) => row.id));
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری لیست");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const headCells = useMemo(
    () => [
      { id: "updateOn", label: "تاریخ", type: "persiandatetime" },
      { id: "fullName", label: "متقاضی" },
      { id: "maskedMobile", label: "موبایل" },
      { id: "referenceID", label: "شناسه درخواست" },
      { id: "plantCapacityTitle", label: "ظرفیت" },
      { id: "powerPlantTypeTitle", label: "نوع نیروگاه" },
      { id: "statusTitle", label: "وضعیت" },
      { id: "stepTitle", label: "مرحله فعلی" },
      { id: "registrationStatusTitle", label: "وضعیت ثبت" },
    ],
    []
  );

  const openRegistration = useCallback(
    (row) => {
      if (!row?.id) return;
      setSearchParams(buildAssistedRegistrationSearchParams(row.id));
    },
    [setSearchParams]
  );

  const onCreateNew = useCallback(() => {
    setSearchParams({ [ASSISTED_REGISTRATION_NEW_QUERY]: "1" });
  }, [setSearchParams]);

  const getRowStatus = (row) => {
    if (row.isLocked) return "primary";
    if (row.canEdit) return "gray";
    return "warning";
  };

  return (
    <PartnerCommissionPageShell
      title="ثبت درخواست برای متقاضی"
      description="لیست متقاضیانی که توسط شما ثبت شده‌اند و وضعیت پرونده و درخواست نیروگاه."
      loading={loading}
      error={error}
      onRetry={load}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="متقاضیان ثبت‌شده"
              action={
                <Box sx={{ display: "flex", alignItems: "center", width: "fit-content" }}>
                  {canManageTeam ? (
                    <Button
                      component={RouterLink}
                      to="/SLN/Contractor/Team"
                      variant="outlined"
                      sx={{ mr: 1 }}
                    >
                      مدیریت تیم
                    </Button>
                  ) : null}
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={onCreateNew}
                  >
                    ایجاد درخواست جدید
                  </Button>
                  <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 1 }} />
                  <IconButton color="primary" onClick={load} disabled={loading}>
                    <SyncIcon />
                  </IconButton>
                </Box>
              }
            />
            <CardContent>
              {!loading && !error && rows.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  هنوز متقاضی ثبت نشده است. با دکمه «ایجاد درخواست جدید» شروع کنید.
                </Alert>
              ) : null}
              <EnhancedTable
                rowKey="id"
                rows={rows}
                headCells={headCells}
                defaultSort={{ orderBy: "updateOn", order: "desc" }}
                pageSize={100}
                actions={[
                  {
                    icon: <ContentPasteSearchIcon />,
                    onClick: openRegistration,
                    color: "primary",
                    tooltip: "مشاهده جزئیات",
                  },
                  {
                    icon: <EditIcon sx={{ width: "1rem", height: "1rem" }} />,
                    onClick: openRegistration,
                    color: "primary",
                    tooltip: "ادامه / ویرایش",
                    hidden: (row) => !row.canEdit,
                  },
                ]}
                getRowStatus={getRowStatus}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PartnerCommissionPageShell>
  );
};

export default ContractorAssistedRegistrationListPage;
